// src/components/solution_sections/SynergisticBenefitsSection.tsx
import React from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Card } from '@dopomogai/ui';
import { renderIcon } from '@dopomogai/utils';
import { INNER_CARD_ANIMATE_RESTING, INNER_CARD_HOVER, INNER_CARD_TRANSITION_HOVER } from '../../shared/animationConstants';

// Define the shape of a single benefit object from our new data structure
interface Benefit {
  id: string;
  icon?: string;
  title: string;
  description: string;
}

// Define the shape of the main data prop for this section
interface BenefitsData {
    sectionTitle?: string;
    icon?: string;
    benefits: Benefit[];
}

// --- BenefitCard Sub-Component (Updated) ---
const BenefitCard: React.FC<{ benefit: Benefit }> = ({ benefit }) => {
  return (
    <motion.div
      animate={INNER_CARD_ANIMATE_RESTING}
      whileHover={INNER_CARD_HOVER}
      transition={INNER_CARD_TRANSITION_HOVER}
      className="bg-white p-5 rounded-xl border-2 border-black flex flex-col items-start h-full"
    >
      <div className="flex items-center gap-3 mb-3">
        <div className="flex-shrink-0 bg-emerald-100 text-emerald-600 p-3 rounded-lg border-2 border-black shadow-sm">
          {renderIcon(benefit.icon || 'star', { size: 24 })}
        </div>
        <h4 className="text-md font-semibold text-gray-800">
            {benefit.title} {/* <-- Renders title directly from data */}
        </h4>
      </div>
      <p className="text-sm text-gray-600 leading-relaxed flex-grow">
          {benefit.description} {/* <-- Renders description directly from data */}
      </p>
    </motion.div>
  );
};


// --- Main Section Component (Updated) ---
interface SynergisticBenefitsSectionProps {
  data: BenefitsData;
  id: string;
  headerBgColor?: string;
}

export const SynergisticBenefitsSection: React.FC<SynergisticBenefitsSectionProps> = ({
  data,
  id,
  headerBgColor,
}) => {
  const { t } = useTranslation('solution_showcase');
  
  if (!data || !data.benefits || data.benefits.length === 0) {
    return null;
  }
  
  const listVariants = {
    visible: { transition: { staggerChildren: 0.1 } },
    hidden: {}
  };

  const itemVariants = {
    visible: { opacity: 1, y: 0, transition: { duration: 0.4, ease: 'easeOut' } },
    hidden: { opacity: 0, y: 20 }
  };

  return (
    <Card
      title={data.sectionTitle || t('benefits.defaultTitle')} // Use data title or fallback to generic i18n
      icon={data.icon || 'star'}
      id={id}
      headerBgColor={headerBgColor}
    >
      <motion.div
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6"
        variants={listVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.2 }}
      >
        {data.benefits.map((benefit) => (
          <motion.div key={benefit.id} variants={itemVariants}>
            <BenefitCard benefit={benefit} /> {/* <-- No longer needs the 't' prop */}
          </motion.div>
        ))}
      </motion.div>
    </Card>
  );
};

